import type { SubjectString } from 'src/auth/utils/defineAbilityFor';
import type {
  Layout,
  LayoutGrid,
  LayoutOption,
} from 'switch-fe-shared/components/Layout/types';
import type { AbilityRole } from 'switch-shared/modules/crud/ability-role/ability-role.entity';
import { ROLES } from 'switch-shared/modules/crud/ability-role/roles.enums';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

import { userStorage } from '../../../utils/userStorage';
import ApplicationGroupOverviewWidget from '../components/widgets/ApplicationGroupOverviewWidget';
import ApplicationsOverviewWidget from '../components/widgets/ApplicationsOverviewWidget';
import BulkMetricWidget from '../components/widgets/BulkMetricWidget';
import CountWidget from '../components/widgets/CountWidget';
import GatewaysWidget from '../components/widgets/GatewaysWidget';
import GroupsLocationListWidget from '../components/widgets/GroupsLocationListWidget';
import GroupsMapWidget from '../components/widgets/GroupsMapWidget';
import LocationListWidget from '../components/widgets/LocationListWidget';
import MapWidget from '../components/widgets/MapWidget';
import MeterPointsTableWidget from '../components/widgets/MeterPointsTableWidget';
import MeterPointsWidget from '../components/widgets/MeterPointsWidget';
import MetersByTagWidget from '../components/widgets/MetersByTagWidget';
import MetersWidget from '../components/widgets/MetersWidget';
import SolarSiteWidget from '../components/widgets/SolarSiteWidget';
import SubMeterMetricWidget from '../components/widgets/SubMeterMetricWidget';
import UnmeteredMetricWidget from '../components/widgets/UnmeteredMetricWidget';
import UsersWidget from '../components/widgets/UsersWidget';
import UtilityUsageWidget from '../components/widgets/UtilityUsageWidget';

export enum Widget {
  COUNT = 'count', // application/account count
  METERS = 'meters', // meter metrics, health, connectivity
  GATEWAYS = 'gateways', // gateway status, connectivity
  USAGE = 'usage', // utility usage split graph
  USAGE_MOBILE = 'usage-mobile', // utility usage graph without metrics (mobile only)
  USAGE_ELECTRICITY = 'usage-electricity', // electricity usage split graph
  USAGE_WATER = 'usage-water', // water usage split graph
  USAGE_GAS = 'usage-gas', // gas usage split graph
  USAGE_ELECTRICITY_MOBILE = 'usage-electricity-mobile', // electricity usage graph without metrics (mobile only)
  USAGE_WATER_MOBILE = 'usage-water-mobile', // water usage graph without metrics (mobile only)
  USAGE_GAS_MOBILE = 'usage-gas-mobile', // gas usage graph without metrics (mobile only)
  BULK_METRIC = 'bulk-metric', // bulk metric card (mobile only)
  SUBMETER_METRIC = 'submeter-metric', // sub-meter metric card (mobile only)
  UNMETERED_METRIC = 'unmetered-metric', // unmetered metric card (mobile only)
  METER_POINTS_TABLE = 'meter-points-table', // custom for BHA
  MAP = 'map', // devices map
  METER_POINTS = 'meter-points', // bulk count
  USERS = 'users', // users count
  LOCATIONS = 'locations', // location list for map navigation
  GROUPS_OVERVIEW = 'groups-overview', // application groups overview table
  GROUPS_MAP = 'groups-map', // application groups map
  GROUPS_LOCATIONS = 'groups-locations', // application groups locations map
  APPLICATIONS_OVERVIEW = 'applications-overview', // applications overview table
  METERS_BY_TAG = 'meters-by-tag', // electricity meters on solar, municipal and other meter points
  SOLAR_PROJECT_KPIS = 'solar-project-kpis', // solar figures since project start against forecast
  SOLAR_YIELD = 'solar-yield', // monthly solar energy against forecast
  SOLAR_SHARE = 'solar-share', // monthly solar used on site and exported, and carbon avoided
  SOLAR_DEMAND_REDUCTION = 'solar-demand-reduction', // monthly coincident demand reduction against forecast
  SOLAR_TOU_SAVINGS = 'solar-tou-savings', // last closed month's solar saving per TOU period
}

// Layout template type for selection
export type LayoutTemplate =
  | 'default'
  | 'executive'
  | 'operational'
  | 'analytics'
  | 'groups-overview'
  | 'bha' // Custom template for Borehole Africa
  | 'ppa-site';

const LAYOUT_TEMPLATES = new Set<LayoutTemplate>([
  'default',
  'executive',
  'operational',
  'analytics',
  'groups-overview',
  'bha',
  'ppa-site',
]);

/** Templates offered only to super admins. */
const SUPER_ADMIN_TEMPLATES = new Set<LayoutTemplate>([
  'analytics',
  'ppa-site',
]);

const DEFAULT_UTILITY_TYPES: UtilityType[] = [
  UtilityType.ELECTRICITY,
  UtilityType.WATER,
  UtilityType.GAS,
];

const USAGE_WIDGET_BY_UTILITY: Record<UtilityType, Widget> = {
  [UtilityType.ELECTRICITY]: Widget.USAGE_ELECTRICITY,
  [UtilityType.WATER]: Widget.USAGE_WATER,
  [UtilityType.GAS]: Widget.USAGE_GAS,
  [UtilityType.FIXED_FEE]: Widget.USAGE,
};

const USAGE_MOBILE_WIDGET_BY_UTILITY: Record<UtilityType, Widget> = {
  [UtilityType.ELECTRICITY]: Widget.USAGE_ELECTRICITY_MOBILE,
  [UtilityType.WATER]: Widget.USAGE_WATER_MOBILE,
  [UtilityType.GAS]: Widget.USAGE_GAS_MOBILE,
  [UtilityType.FIXED_FEE]: Widget.USAGE_MOBILE,
};

const resolveUsageUtilities = (
  availableUtilities?: UtilityType[]
): UtilityType[] => {
  const utilities =
    availableUtilities && availableUtilities.length > 0
      ? availableUtilities
      : DEFAULT_UTILITY_TYPES;

  return utilities.filter(
    (utilityType) =>
      utilityType !== UtilityType.FIXED_FEE &&
      Object.prototype.hasOwnProperty.call(USAGE_WIDGET_BY_UTILITY, utilityType)
  );
};

// BHA LAYOUT - Custom template for Borehole Africa
export function getBHATemplate(
  isAllAppsView: boolean,
  isAllUtilities = false,
  isSuperAdmin = true,
  availableUtilities?: UtilityType[]
): LayoutGrid<Widget> {
  const hideMetrics = !isSuperAdmin && isAllUtilities;
  const hideTrends = !isSuperAdmin;
  const usageUtilities = resolveUsageUtilities(availableUtilities);
  const includePerUtilityUsage = isAllUtilities && !isAllAppsView;
  const perUtilityUsageComponents = includePerUtilityUsage
    ? {
        [Widget.USAGE_ELECTRICITY]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.ELECTRICITY}
              hideMetrics={hideMetrics}
            />
          ),
        },
        [Widget.USAGE_WATER]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.WATER}
              hideMetrics={hideMetrics}
            />
          ),
        },
        [Widget.USAGE_GAS]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.GAS}
              hideMetrics={hideMetrics}
            />
          ),
        },
        [Widget.USAGE_ELECTRICITY_MOBILE]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.ELECTRICITY}
              hideTotals
            />
          ),
        },
        [Widget.USAGE_WATER_MOBILE]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.WATER}
              hideTotals
            />
          ),
        },
        [Widget.USAGE_GAS_MOBILE]: {
          component: (
            <UtilityUsageWidget utilityOverride={UtilityType.GAS} hideTotals />
          ),
        },
      }
    : {};

  return {
    components: {
      [Widget.COUNT]: { component: <CountWidget /> },
      [Widget.METERS]: { component: <MetersWidget /> },
      [Widget.METER_POINTS]: { component: <MeterPointsWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE]: {
        component: <UtilityUsageWidget hideMetrics={hideMetrics} />,
      },
      [Widget.USAGE_MOBILE]: { component: <UtilityUsageWidget hideTotals /> },
      ...perUtilityUsageComponents,
      [Widget.BULK_METRIC]: {
        component: <BulkMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.SUBMETER_METRIC]: {
        component: <SubMeterMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.UNMETERED_METRIC]: {
        component: <UnmeteredMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.MAP]: { component: <MapWidget /> },
      [Widget.LOCATIONS]: { component: <LocationListWidget /> },
      [Widget.APPLICATIONS_OVERVIEW]: {
        component: <ApplicationsOverviewWidget />,
      },
      [Widget.METER_POINTS_TABLE]: {
        component: <MeterPointsTableWidget key='executive-apps' pageSize={7} />,
      },
    },
    layouts: {
      xxs: [
        { i: Widget.COUNT, w: 12, h: 3 },
        { i: Widget.METERS, w: 12, h: 3 },
        { i: Widget.METER_POINTS, w: 12, h: 3 },
        { i: Widget.GATEWAYS, w: 12, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.METER_POINTS_TABLE, w: 12, h: 8 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
      ],
      xs: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.METER_POINTS_TABLE, w: 12, h: 10 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
      ],
      sm: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.METER_POINTS_TABLE, w: 12, h: 10 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
        { i: Widget.MAP, w: 12, h: 10 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
      ],
      md: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 11,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE, w: 12, h: 11 } as Layout<Widget>]),
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 7 } as Layout<Widget>]
          : []),
        { i: Widget.METER_POINTS_TABLE, w: 12, h: 7 },
        { i: Widget.MAP, w: 8, h: 11 },
        { i: Widget.LOCATIONS, w: 4, h: 11 },
      ],
      lg: [
        { i: Widget.COUNT, w: 3, h: 3 },
        { i: Widget.METERS, w: 3, h: 3 },
        { i: Widget.METER_POINTS, w: 3, h: 3 },
        { i: Widget.GATEWAYS, w: 3, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 11,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE, w: 12, h: 11 } as Layout<Widget>]),
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 7 } as Layout<Widget>]
          : []),
        { i: Widget.METER_POINTS_TABLE, w: 12, h: 7 },
        { i: Widget.MAP, w: 9, h: 11 },
        { i: Widget.LOCATIONS, w: 3, h: 11 },
      ],
    },
  };
}

// EXECUTIVE LAYOUT - Minimalist with essential metrics
export function getExecutiveTemplate(
  isAllAppsView: boolean,
  isAllUtilities = false,
  isSuperAdmin = true,
  availableUtilities?: UtilityType[]
): LayoutGrid<Widget> {
  const hideMetrics = !isSuperAdmin && isAllUtilities;
  const hideTrends = !isSuperAdmin;
  const usageUtilities = resolveUsageUtilities(availableUtilities);
  const includePerUtilityUsage = isAllUtilities && !isAllAppsView;
  const perUtilityUsageComponents = includePerUtilityUsage
    ? {
        [Widget.USAGE_ELECTRICITY]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.ELECTRICITY}
              hideMetrics={hideMetrics}
              pageSize={10}
            />
          ),
        },
        [Widget.USAGE_WATER]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.WATER}
              hideMetrics={hideMetrics}
              pageSize={10}
            />
          ),
        },
        [Widget.USAGE_GAS]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.GAS}
              hideMetrics={hideMetrics}
              pageSize={10}
            />
          ),
        },
        [Widget.USAGE_ELECTRICITY_MOBILE]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.ELECTRICITY}
              hideTotals
              pageSize={10}
            />
          ),
        },
        [Widget.USAGE_WATER_MOBILE]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.WATER}
              hideTotals
              pageSize={10}
            />
          ),
        },
        [Widget.USAGE_GAS_MOBILE]: {
          component: (
            <UtilityUsageWidget
              utilityOverride={UtilityType.GAS}
              hideTotals
              pageSize={10}
            />
          ),
        },
      }
    : {};

  return {
    components: {
      [Widget.COUNT]: { component: <CountWidget /> },
      [Widget.METERS]: { component: <MetersWidget /> },
      [Widget.METER_POINTS]: { component: <MeterPointsWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE]: {
        component: (
          <UtilityUsageWidget hideMetrics={hideMetrics} pageSize={10} />
        ),
      },
      [Widget.USAGE_MOBILE]: { component: <UtilityUsageWidget hideTotals /> },
      ...perUtilityUsageComponents,
      [Widget.BULK_METRIC]: {
        component: <BulkMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.SUBMETER_METRIC]: {
        component: <SubMeterMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.UNMETERED_METRIC]: {
        component: <UnmeteredMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.MAP]: { component: <MapWidget /> },
      [Widget.LOCATIONS]: { component: <LocationListWidget /> },
      [Widget.APPLICATIONS_OVERVIEW]: {
        component: <ApplicationsOverviewWidget />,
      },
    },
    layouts: {
      xxs: [
        { i: Widget.COUNT, w: 12, h: 3 },
        { i: Widget.METERS, w: 12, h: 3 },
        { i: Widget.METER_POINTS, w: 12, h: 3 },
        { i: Widget.GATEWAYS, w: 12, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
      ],
      xs: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
      ],
      sm: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_MOBILE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 8,
                }) as Layout<Widget>
            )
          : [{ i: Widget.USAGE_MOBILE, w: 12, h: 8 } as Layout<Widget>]),
        { i: Widget.BULK_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.SUBMETER_METRIC, w: 12, h: 3 } as Layout<Widget>,
        { i: Widget.UNMETERED_METRIC, w: 12, h: 3 } as Layout<Widget>,
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 8 } as Layout<Widget>]
          : []),
        { i: Widget.MAP, w: 12, h: 10 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
      ],
      md: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 7,
                }) as Layout<Widget>
            )
          : [
              {
                i: Widget.USAGE,
                w: 12,
                h: isAllAppsView ? 11 : 7,
              } as Layout<Widget>,
            ]),
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 7 } as Layout<Widget>]
          : []),
        { i: Widget.MAP, w: 8, h: 11 },
        { i: Widget.LOCATIONS, w: 4, h: 11 },
      ],
      lg: [
        { i: Widget.COUNT, w: 3, h: 3 },
        { i: Widget.METERS, w: 3, h: 3 },
        { i: Widget.METER_POINTS, w: 3, h: 3 },
        { i: Widget.GATEWAYS, w: 3, h: 3 },
        ...(isAllUtilities && !isAllAppsView
          ? usageUtilities.map(
              (utilityType) =>
                ({
                  i: USAGE_WIDGET_BY_UTILITY[utilityType],
                  w: 12,
                  h: 7,
                }) as Layout<Widget>
            )
          : [
              {
                i: Widget.USAGE,
                w: 12,
                h: isAllAppsView ? 11 : 7,
              } as Layout<Widget>,
            ]),
        ...(isAllAppsView
          ? [{ i: Widget.APPLICATIONS_OVERVIEW, w: 12, h: 7 } as Layout<Widget>]
          : []),
        { i: Widget.MAP, w: 9, h: 11 },
        { i: Widget.LOCATIONS, w: 3, h: 11 },
      ],
    },
  };
}

// OPERATIONAL LAYOUT - Comprehensive with larger widgets
export function getOperationalTemplate(
  isAllAppsView: boolean,
  isAllUtilities = false,
  isSuperAdmin = true
): LayoutGrid<Widget> {
  const hideMetrics = !isSuperAdmin && isAllUtilities;
  const hideTrends = !isSuperAdmin;

  return {
    components: {
      [Widget.METERS]: { component: <MetersWidget /> },
      [Widget.METER_POINTS]: { component: <MeterPointsWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE]: {
        component: (
          <UtilityUsageWidget hideMetrics={hideMetrics} pageSize={10} />
        ),
      },
      [Widget.USAGE_MOBILE]: {
        component: <UtilityUsageWidget hideTotals pageSize={10} />,
      },
      [Widget.BULK_METRIC]: {
        component: <BulkMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.SUBMETER_METRIC]: {
        component: <SubMeterMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.UNMETERED_METRIC]: {
        component: <UnmeteredMetricWidget hideTrends={hideTrends} />,
      },
      [Widget.MAP]: { component: <MapWidget /> },
      [Widget.LOCATIONS]: { component: <LocationListWidget /> },
      [Widget.APPLICATIONS_OVERVIEW]: {
        component: <ApplicationsOverviewWidget pageSize={10} />,
      },
    },
    layouts: {
      xxs: [
        { i: Widget.METERS, w: 12, h: 3 },
        { i: Widget.METER_POINTS, w: 12, h: 3 },
        { i: Widget.GATEWAYS, w: 12, h: 3 },
        { i: Widget.MAP, w: 12, h: 5 },
        { i: Widget.LOCATIONS, w: 12, h: 4 },
        ...(isAllAppsView
          ? [
              {
                i: Widget.APPLICATIONS_OVERVIEW,
                w: 12,
                h: 8,
              } as Layout<Widget>,
            ]
          : []),
      ],
      xs: [
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.MAP, w: 12, h: 12 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
        ...(isAllAppsView
          ? [
              {
                i: Widget.APPLICATIONS_OVERVIEW,
                w: 12,
                h: 12,
              } as Layout<Widget>,
            ]
          : []),
      ],
      sm: [
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.MAP, w: 12, h: 12 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
        ...(isAllAppsView
          ? [
              {
                i: Widget.APPLICATIONS_OVERVIEW,
                w: 12,
                h: 12,
              } as Layout<Widget>,
            ]
          : []),
      ],
      md: [
        { i: Widget.METERS, w: 4, h: 3 },
        { i: Widget.METER_POINTS, w: 4, h: 3 },
        { i: Widget.GATEWAYS, w: 4, h: 3 },
        { i: Widget.MAP, w: 8, h: 12 },
        { i: Widget.LOCATIONS, w: 4, h: 12 },
        ...(isAllAppsView
          ? [
              {
                i: Widget.APPLICATIONS_OVERVIEW,
                w: 12,
                h: 12,
              } as Layout<Widget>,
            ]
          : []),
      ],
      lg: [
        { i: Widget.METERS, w: 4, h: 3 },
        { i: Widget.METER_POINTS, w: 4, h: 3 },
        { i: Widget.GATEWAYS, w: 4, h: 3 },
        { i: Widget.MAP, w: 9, h: 7 },
        { i: Widget.LOCATIONS, w: 3, h: 7 },
        ...(isAllAppsView
          ? [
              {
                i: Widget.APPLICATIONS_OVERVIEW,
                w: 12,
                h: 11,
              } as Layout<Widget>,
            ]
          : []),
      ],
    },
  };
}

// ANALYTICS LAYOUT - Chart-centric with emphasis on data visualization
export function getAnalyticsTemplate(
  isAllUtilities = false,
  isSuperAdmin = true
): LayoutGrid<Widget> {
  const hideMetrics = !isSuperAdmin && isAllUtilities;

  return {
    components: {
      [Widget.COUNT]: { component: <CountWidget /> },
      [Widget.METERS]: { component: <MetersWidget /> },
      [Widget.METER_POINTS]: { component: <MeterPointsWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE]: {
        component: <UtilityUsageWidget hideMetrics={hideMetrics} />,
      },
      [Widget.MAP]: { component: <MapWidget /> },
      [Widget.LOCATIONS]: { component: <LocationListWidget /> },
    },
    layouts: {
      xxs: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USAGE, w: 12, h: 12 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.MAP, w: 12, h: 10 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
      ],
      xs: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USAGE, w: 12, h: 12 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.MAP, w: 12, h: 10 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
      ],
      sm: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USAGE, w: 12, h: 12 },
        { i: Widget.METER_POINTS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.MAP, w: 12, h: 10 },
        { i: Widget.LOCATIONS, w: 12, h: 6 },
      ],
      md: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USAGE, w: 12, h: 10 },
        { i: Widget.METER_POINTS, w: 3, h: 3 },
        { i: Widget.GATEWAYS, w: 3, h: 3 },
        { i: Widget.MAP, w: 4, h: 8 },
        { i: Widget.LOCATIONS, w: 2, h: 8 },
      ],
      lg: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USAGE, w: 12, h: 10 },
        { i: Widget.METER_POINTS, w: 3, h: 3 },
        { i: Widget.GATEWAYS, w: 3, h: 3 },
        { i: Widget.MAP, w: 4, h: 8 },
        { i: Widget.LOCATIONS, w: 2, h: 8 },
      ],
    },
  };
}

// SOLAR SAVINGS LAYOUT - Solar yield, savings and irradiance for one site
// PPA / RRA SITE LAYOUT - Site meters, electricity usage and solar performance
export const getPpaSiteTemplate = (
  isAllAppsView: boolean
): LayoutGrid<Widget> => {
  const cards = (w: Layout<Widget>['w']): Layout<Widget>[] => [
    { i: Widget.COUNT, w, h: 3 },
    { i: Widget.METERS_BY_TAG, w, h: 3 },
    { i: Widget.METER_POINTS, w, h: 3 },
    { i: Widget.GATEWAYS, w, h: 3 },
  ];
  const usage: Layout<Widget> = {
    i: Widget.USAGE_ELECTRICITY,
    w: 12,
    h: isAllAppsView ? 11 : 7,
  };
  const stackedSolar = (kpiHeight: Layout<Widget>['h']): Layout<Widget>[] => [
    { i: Widget.SOLAR_PROJECT_KPIS, w: 12, h: kpiHeight },
    { i: Widget.SOLAR_YIELD, w: 12, h: 7 },
    { i: Widget.SOLAR_SHARE, w: 12, h: 7 },
    { i: Widget.SOLAR_DEMAND_REDUCTION, w: 12, h: 7 },
    { i: Widget.SOLAR_TOU_SAVINGS, w: 12, h: 6 },
  ];
  const mobile: Layout<Widget>[] = [
    ...cards(6),
    { i: Widget.USAGE_ELECTRICITY_MOBILE, w: 12, h: 8 },
    ...stackedSolar(5),
  ];

  return {
    components: {
      [Widget.COUNT]: { component: <CountWidget /> },
      [Widget.METERS_BY_TAG]: { component: <MetersByTagWidget /> },
      [Widget.METER_POINTS]: { component: <MeterPointsWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE_ELECTRICITY]: {
        component: (
          <UtilityUsageWidget
            utilityOverride={UtilityType.ELECTRICITY}
            pageSize={10}
          />
        ),
      },
      [Widget.USAGE_ELECTRICITY_MOBILE]: {
        component: (
          <UtilityUsageWidget
            utilityOverride={UtilityType.ELECTRICITY}
            hideTotals
            pageSize={10}
          />
        ),
      },
      [Widget.SOLAR_PROJECT_KPIS]: {
        component: <SolarSiteWidget view='kpis' />,
      },
      [Widget.SOLAR_YIELD]: { component: <SolarSiteWidget view='yield' /> },
      [Widget.SOLAR_SHARE]: { component: <SolarSiteWidget view='share' /> },
      [Widget.SOLAR_DEMAND_REDUCTION]: {
        component: <SolarSiteWidget view='demand' />,
      },
      [Widget.SOLAR_TOU_SAVINGS]: {
        component: <SolarSiteWidget view='savings' />,
      },
    },
    layouts: {
      xxs: [
        ...cards(12),
        { i: Widget.USAGE_ELECTRICITY_MOBILE, w: 12, h: 8 },
        ...stackedSolar(6),
      ],
      xs: mobile,
      sm: mobile,
      md: [...cards(6), usage, ...stackedSolar(5)],
      lg: [
        ...cards(3),
        usage,
        { i: Widget.SOLAR_PROJECT_KPIS, w: 4, h: 7 },
        { i: Widget.SOLAR_YIELD, w: 8, h: 7 },
        { i: Widget.SOLAR_SHARE, w: 6, h: 7 },
        { i: Widget.SOLAR_DEMAND_REDUCTION, w: 6, h: 7 },
        { i: Widget.SOLAR_TOU_SAVINGS, w: 12, h: 5 },
      ],
    },
  };
};

// GROUPS OVERVIEW LAYOUT - Full-screen application groups overview
export const getGroupsOverviewTemplate = (
  isAllUtilities = false,
  isSuperAdmin = true
): LayoutGrid<Widget> => {
  const hideMetrics = !isSuperAdmin && isAllUtilities;

  return {
    components: {
      [Widget.COUNT]: { component: <CountWidget /> },
      [Widget.METERS]: { component: <MetersWidget /> },
      [Widget.USERS]: { component: <UsersWidget /> },
      [Widget.GATEWAYS]: { component: <GatewaysWidget /> },
      [Widget.USAGE]: {
        component: <UtilityUsageWidget hideMetrics={hideMetrics} />,
      },
      [Widget.GROUPS_MAP]: { component: <GroupsMapWidget /> },
      [Widget.GROUPS_LOCATIONS]: { component: <GroupsLocationListWidget /> },
      [Widget.GROUPS_OVERVIEW]: {
        component: <ApplicationGroupOverviewWidget />,
      },
    },
    layouts: {
      xxs: [
        { i: Widget.COUNT, w: 12, h: 3 },
        { i: Widget.METERS, w: 12, h: 3 },
        { i: Widget.USERS, w: 12, h: 3 },
        { i: Widget.GATEWAYS, w: 12, h: 3 },
        { i: Widget.GROUPS_OVERVIEW, w: 12, h: 7 },
        { i: Widget.GROUPS_MAP, w: 12, h: 8 },
        { i: Widget.GROUPS_LOCATIONS, w: 12, h: 6 },
      ],
      xs: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USERS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.GROUPS_OVERVIEW, w: 12, h: 7 },
        { i: Widget.GROUPS_MAP, w: 12, h: 10 },
        { i: Widget.GROUPS_LOCATIONS, w: 12, h: 6 },
      ],
      sm: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USERS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.GROUPS_OVERVIEW, w: 12, h: 7 },
        { i: Widget.GROUPS_MAP, w: 12, h: 10 },
        { i: Widget.GROUPS_LOCATIONS, w: 12, h: 6 },
      ],
      md: [
        { i: Widget.COUNT, w: 6, h: 3 },
        { i: Widget.METERS, w: 6, h: 3 },
        { i: Widget.USERS, w: 6, h: 3 },
        { i: Widget.GATEWAYS, w: 6, h: 3 },
        { i: Widget.GROUPS_OVERVIEW, w: 12, h: 7 },
        { i: Widget.GROUPS_MAP, w: 8, h: 11 },
        { i: Widget.GROUPS_LOCATIONS, w: 4, h: 11 },
      ],
      lg: [
        { i: Widget.COUNT, w: 3, h: 3 },
        { i: Widget.METERS, w: 3, h: 3 },
        { i: Widget.USERS, w: 3, h: 3 },
        { i: Widget.GATEWAYS, w: 3, h: 3 },
        { i: Widget.GROUPS_OVERVIEW, w: 12, h: 7 },
        { i: Widget.GROUPS_MAP, w: 9, h: 11 },
        { i: Widget.GROUPS_LOCATIONS, w: 3, h: 11 },
      ],
    },
  };
};

// Get layout by template
export function getDashboardLayoutByTemplate(
  template: LayoutTemplate = 'default',
  isAllAppsView = true,
  isAllUtilities = false,
  isSuperAdmin = true,
  availableUtilities?: UtilityType[]
): LayoutGrid<Widget> {
  switch (template) {
    case 'executive':
      return getExecutiveTemplate(
        isAllAppsView,
        isAllUtilities,
        isSuperAdmin,
        availableUtilities
      );
    case 'operational':
      return getOperationalTemplate(
        isAllAppsView,
        isAllUtilities,
        isSuperAdmin
      );
    case 'analytics':
      return getAnalyticsTemplate(isAllUtilities, isSuperAdmin);
    case 'bha':
      return getBHATemplate(
        isAllAppsView,
        isAllUtilities,
        isSuperAdmin,
        availableUtilities
      );
    case 'groups-overview':
      return getGroupsOverviewTemplate(isAllUtilities, isSuperAdmin);
    case 'ppa-site':
      return isSuperAdmin
        ? getPpaSiteTemplate(isAllAppsView)
        : getExecutiveTemplate(
            isAllAppsView,
            isAllUtilities,
            isSuperAdmin,
            availableUtilities
          );
    default:
      return getExecutiveTemplate(
        isAllAppsView,
        isAllUtilities,
        isSuperAdmin,
        availableUtilities
      );
  }
}

/** The subject a user must be able to read to see a widget, or `undefined` for widgets everyone sees. */
export function widgetSubject(
  widget: Widget,
  hasApplication: boolean
): SubjectString | undefined {
  switch (widget) {
    case Widget.COUNT:
      return hasApplication ? 'Accounts' : 'Applications';
    case Widget.METERS:
    case Widget.METERS_BY_TAG:
      return 'Meters';
    case Widget.METER_POINTS:
      return 'MeterPoints';
    case Widget.GATEWAYS:
      return 'Gateways';
    case Widget.USERS:
      return 'Users';
    case Widget.APPLICATIONS_OVERVIEW:
      return 'Applications';
    case Widget.GROUPS_OVERVIEW:
    case Widget.GROUPS_LOCATIONS:
      return 'ApplicationGroups';
    default:
      return undefined;
  }
}

// Helper to check if a layout template requires SUPER_ADMIN role
function requiresSuperAdmin(template: LayoutTemplate): boolean {
  return SUPER_ADMIN_TEMPLATES.has(template);
}

// Helper to check if user has access to a specific layout template
function hasLayoutAccess(
  template: LayoutTemplate,
  userRoles: Array<{ role: ROLES }>
): boolean {
  if (!requiresSuperAdmin(template)) {
    return true;
  }
  return userRoles.some((role) => role.role === ROLES.SUPER_ADMIN);
}

// Helper to check if user is a BHA user (has access to specific application group)
function isBHAUser(userRoles: Array<AbilityRole>): boolean {
  return userRoles.some((role) =>
    role.scope
      ?.map((ele) => ele.applicationGroupId)
      .includes('5fd8e2aa5cdb85aa9a69430f')
  );
}

/** Helper to get available layout options filtered by user access */
export function getAvailableLayoutOptions(
  userRoles: Array<AbilityRole>,
  isAllGroupsSelected: boolean,
  allLayoutOptions: LayoutOption<LayoutTemplate>[]
): LayoutOption<LayoutTemplate>[] {
  let options = allLayoutOptions;

  // If BHA user, only show their template
  if (isBHAUser(userRoles)) {
    options = options.filter((option) => option.template === 'bha');
  } else if (!userRoles.some((role) => role.role === ROLES.SUPER_ADMIN)) {
    // If not SUPER_ADMIN role, only show non-BHA templates
    options = options.filter((option) => option.template !== 'bha');
  }

  // Filter out layouts the user doesn't have access to
  options = options.filter((option) =>
    hasLayoutAccess(option.template, userRoles)
  );

  // When "All Groups" is selected, only show groups-overview layout
  if (isAllGroupsSelected) {
    options = options.filter((option) => option.template === 'groups-overview');
  } else {
    // When a specific group is selected, hide groups-overview from options
    options = options.filter((option) => option.template !== 'groups-overview');
  }

  return options;
}

/** Helper to validate and get user's saved template, resetting to default if no access */
export function getValidatedUserTemplate(
  userRoles: Array<AbilityRole>
): LayoutTemplate {
  const savedTemplate = userStorage.getItem(
    'dashboard-template'
  ) as LayoutTemplate;
  const defaultTemplate = isBHAUser(userRoles) ? 'bha' : 'default';
  const template = savedTemplate ?? defaultTemplate;

  if (
    !LAYOUT_TEMPLATES.has(template) ||
    !hasLayoutAccess(template, userRoles)
  ) {
    userStorage.setItem('dashboard-template', defaultTemplate);
    return defaultTemplate;
  }

  return template;
}
